/**
 * Per-Space payload encryption (Argon2 space key + AEAD AAD).
 *
 * Space messages and structural metadata MUST be sealed with the cached
 * Argon2id per-Space key (`deriveSpaceCipherKey`), not the unbound Community
 * Cipher master key. Associated data binds each ciphertext to a domain, Space,
 * entity, and field so a compromised server cannot replay blobs across
 * channels or metadata slots.
 *
 * New ciphertext always includes AAD. Decrypt of historical (pre-AAD) payloads
 * falls back to `cipher.key` with no AAD when `allowLegacyUntagged` is set.
 *
 * @module crypto/ciphers/spacePayload
 */
import { encrypt, decrypt } from '../encrypt';
import { toBytes } from '../utils';
/** AEAD domain for Space payload associated data. */
export const SPACE_PAYLOAD_AAD_DOMAIN = 'adieuu-space-aad-v1';
/**
 * Stable AAD entity ids for seed blobs created before the server assigns
 * category/channel/role ObjectIds. Decrypt of those blobs must keep using
 * these sentinels for the lifetime of the ciphertext.
 */
export const SPACE_SEED_ENTITY = {
    category: 'seed-category',
    channel: 'seed-channel',
    roleAdmin: 'seed-role-admin',
    roleEveryone: 'seed-role-everyone',
    newCategory: 'new-category',
    newChannel: 'new-channel',
    newRole: 'new-role',
};
function requireAadPart(label, value) {
    if (!value) {
        throw new Error(`Space payload AAD ${label} is required`);
    }
    if (value.includes('\n')) {
        throw new Error(`Space payload AAD ${label} must not contain newlines`);
    }
}
/**
 * Encodes Space payload AAD as `domain \\n spaceId \\n entityId \\n field`.
 *
 * None of the bound fields may contain a newline, so the encoding is
 * unambiguous.
 */
export function encodeSpacePayloadAad(aad) {
    requireAadPart('spaceId', aad.spaceId);
    requireAadPart('entityId', aad.entityId);
    requireAadPart('field', aad.field);
    return toBytes([SPACE_PAYLOAD_AAD_DOMAIN, aad.spaceId, aad.entityId, aad.field].join('\n'));
}
function isAadList(aad) {
    return Array.isArray(aad);
}
function aadList(aad) {
    return isAadList(aad) ? [...aad] : [aad];
}
/**
 * Encrypts plaintext under the per-Space Argon2 key with context AAD.
 *
 * `cipher.cipherId` is stored for routing; the AEAD key is `spaceKey`.
 */
export function encryptWithSpaceKey(cipher, spaceKey, plaintext, aad) {
    if (spaceKey.length < 16) {
        throw new Error('Space key must be at least 16 bytes');
    }
    const associatedData = encodeSpacePayloadAad(aad);
    const { ciphertext, nonce } = encrypt(spaceKey, plaintext, cipher.profile, undefined, associatedData);
    return {
        ciphertext,
        nonce,
        cipherId: cipher.cipherId,
    };
}
/**
 * Decrypts a Space payload.
 *
 * Tries `spaceKey` + each AAD candidate first. When `allowLegacyUntagged` is
 * true (default), falls back to `cipher.key` with no AAD for historical
 * ciphertext that predates this binding.
 */
export function decryptWithSpaceKey(cipher, spaceKey, payload, aad, options) {
    if (cipher.cipherId !== payload.cipherId) {
        throw new Error('Cipher ID mismatch - wrong cipher for this payload');
    }
    if (spaceKey.length < 16) {
        throw new Error('Space key must be at least 16 bytes');
    }
    for (const ctx of aadList(aad)) {
        try {
            const plaintext = decrypt(spaceKey, payload.ciphertext, payload.nonce, cipher.profile, encodeSpacePayloadAad(ctx));
            return { plaintext, usedLegacyUntagged: false };
        }
        catch {
            // Try the next AAD candidate (seed sentinel vs real id).
        }
    }
    const allowLegacy = options?.allowLegacyUntagged !== false;
    if (allowLegacy) {
        const plaintext = decrypt(cipher.key, payload.ciphertext, payload.nonce, cipher.profile);
        return { plaintext, usedLegacyUntagged: true };
    }
    throw new Error('Failed to decrypt space payload');
}
//# sourceMappingURL=spacePayload.js.map